import { APOSTILLE_COUNTRIES, APOSTILLE_DOC_TYPES } from "@/data/apostille/keywords-500";
import { APOSTILLE_STATUS } from "@/data/legal-facts";

export type ApostilleCountry = (typeof APOSTILLE_COUNTRIES)[number];
export type ApostilleDoc = (typeof APOSTILLE_DOC_TYPES)[number];

export type FaqItem = { q: string; a: string };

/**
* Steps describe the chain that is legally correct TODAY (pre-28 Feb 2027):
* issuing authority / notary -> MFA legalisation -> destination embassy.
* After entry into force the embassy step drops for Contracting States.
*/
export const APOSTILLE_BASE_STEPS = [
  "ตรวจเอกสารต้นฉบับและออกคำแนะนำก่อนยื่น (ตรวจตราประทับ ลายมือชื่อผู้มีอำนาจ และอายุเอกสาร)",
  "คัดสำเนาใหม่จากหน่วยงานต้นทาง (อำเภอ/เขต ทะเบียนราษฎร สถานศึกษา หรือ DBD) หากเอกสารเก่าเกินเงื่อนไขปลายทาง",
  "แปลเอกสารเป็นภาษาอังกฤษหรือภาษาปลายทาง โดยผู้แปลที่กรมการกงสุลยอมรับ และรับรองคำแปลให้ครบทุกตราประทับ",
  "ยื่นรับรองนิติกรณ์ที่กองสัญชาติและนิติกรณ์ กรมการกงสุล (แจ้งวัฒนะ) หรือสาขาที่เปิดให้บริการ",
  `นำเอกสารที่ผ่านกรมการกงสุลไปรับรองที่สถานทูต/สถานกงสุลประเทศปลายทาง เฉพาะประเทศที่ยังกำหนด (ขั้นตอนนี้จะลดลงสำหรับประเทศภาคี หลังอนุสัญญามีผลกับไทยวันที่ ${APOSTILLE_STATUS.inForceDateTh})`,
  "ตรวจความถูกต้องของตรารับรอง เลขทะเบียน และวันที่ ก่อนจัดส่งถึงมือคุณทั่วประเทศหรือส่งต่อไปต่างประเทศ",
];

export const APOSTILLE_PRICE_NOTE =
  "ค่าบริการขึ้นกับประเภทเอกสาร จำนวนฉบับ ภาษา ประเทศปลายทาง และกรอบเวลา โดยแยกค่าดำเนินการ ค่าธรรมเนียมราชการ ค่าธรรมเนียมสถานทูต และค่าแปลรับรองในใบเสนอราคา";

/** Reusable, factually-correct explanation of where Thailand stands right now. */
export const APOSTILLE_TRANSITION_FAQ: FaqItem = {
  q: "ตอนนี้ประเทศไทยออกตรา Apostille ได้แล้วหรือยัง?",
  a: `ยังไม่ได้ครับ ไทยยื่นภาคยานุวัติสารเข้าเป็นภาคีอนุสัญญากรุงเฮก ค.ศ. 1961 เมื่อ ${APOSTILLE_STATUS.accessionDateTh} และอนุสัญญาจะมีผลบังคับใช้กับไทยในวันที่ ${APOSTILLE_STATUS.inForceDateTh} ระหว่างนี้เอกสารไทยยังต้องใช้สายรับรองเดิม คือ ผู้ออกเอกสาร/Notary → รับรองนิติกรณ์ที่กรมการกงสุล → รับรองสถานทูตปลายทางเฉพาะประเทศที่ยังกำหนด (อ้างอิง: HCCH และกรมการกงสุล)`,
};

export function countryFaqs(c: ApostilleCountry): FaqItem[] {
  return [
    APOSTILLE_TRANSITION_FAQ,
    {
      q: `เอกสารไทยที่จะใช้ใน${c.th} ตอนนี้ต้องรับรองอย่างไร?`,
      a: `ขณะนี้ต้องรับรองนิติกรณ์ที่กรมการกงสุลก่อน แล้วจึงนำไปรับรองที่สถานทูต${c.th} ประจำประเทศไทยตามที่ปลายทางกำหนด และเมื่ออนุสัญญามีผลกับไทยในวันที่ ${APOSTILLE_STATUS.inForceDateTh} เอกสารที่มีตรา Apostille จะใช้ที่${c.th}ได้โดยไม่ต้องผ่านสถานทูตอีก — ${c.note}`,
    },
    {
      q: `เตรียมเอกสารไป${c.th} ใช้เวลากี่วัน?`,
      a: `ขั้นตอนกรมการกงสุลแบบปกติใช้เวลาประมาณ 3-5 วันทำการนับจากได้รับเอกสารครบ หากรวมการคัดเอกสารใหม่ การแปลรับรอง และคิวรับรองของสถานทูต${c.th} ควรเผื่อรวม 7-14 วันทำการ ทั้งนี้ขึ้นกับคิวจริงของแต่ละหน่วยงาน`,
    },
    {
      q: `ต้องแปลเอกสารก่อนยื่นรับรองเพื่อใช้ที่${c.th}ไหม?`,
      a: `แนะนำให้แปลก่อนเสมอ เพราะการรับรองจะครอบคลุมลายมือชื่อผู้รับรองคำแปลด้วย ทำให้ต้นฉบับและคำแปลเดินเป็นชุดเดียวกันและลดโอกาสถูกตีกลับที่${c.th}`,
    },
    {
      q: `ค่าใช้จ่ายในการรับรองเอกสารสำหรับ${c.th}เท่าไหร่?`,
      a: APOSTILLE_PRICE_NOTE,
    },
    {
      q: `อยู่ต่างจังหวัดหรืออยู่ที่${c.th}อยู่แล้ว ใช้บริการได้ไหม?`,
      a: `ได้ครับ เรารับมอบอำนาจดำเนินการแทนเต็มรูปแบบ รับ-ส่งเอกสารทางไปรษณีย์/EMS หรือขนส่งระหว่างประเทศ พร้อมแจ้งสถานะทุกขั้นตอนทาง LINE`,
    },
  ];
}

export function docFaqs(d: ApostilleDoc): FaqItem[] {
  return [
    APOSTILLE_TRANSITION_FAQ,
    {
      q: `รับรอง${d.th}เพื่อใช้ต่างประเทศ ต้องใช้เอกสารอะไรบ้าง?`,
      a: `ใช้ ${d.th} ฉบับจริงหรือฉบับคัดรับรองจากหน่วยงานราชการ พร้อมสำเนาบัตรประชาชน/หนังสือเดินทางของเจ้าของเอกสาร และหนังสือมอบอำนาจกรณีให้เราดำเนินการแทน`,
    },
    {
      q: `${d.th} ต้องคัดฉบับใหม่ก่อนยื่นรับรองไหม?`,
      a: `หากเอกสารเก่ามาก ตราประทับเลือน หรือเป็นสำเนาที่ไม่มีลายมือชื่อเจ้าหน้าที่ ควรคัดฉบับใหม่ก่อน เพราะกรมการกงสุลตรวจลายมือชื่อผู้ลงนามเทียบกับตัวอย่างลายมือชื่อที่มีอยู่`,
    },
    {
      q: `รับรอง${d.th} ใช้เวลาและค่าใช้จ่ายเท่าไหร่?`,
      a: `ขั้นตอนกรมการกงสุลปกติ 3-5 วันทำการ · ${APOSTILLE_PRICE_NOTE}`,
    },
    {
      q: `${d.th} ที่รับรองแล้วใช้ได้กี่ประเทศ?`,
      a: `ปัจจุบันใช้ได้กับทุกประเทศ แต่ต้องผ่านการรับรองของสถานทูตปลายทางตามที่แต่ละประเทศกำหนด และเมื่ออนุสัญญากรุงเฮกมีผลกับไทยในวันที่ ${APOSTILLE_STATUS.inForceDateTh} เอกสารที่มีตรา Apostille จะใช้กับประเทศภาคีได้โดยไม่ต้องผ่านสถานทูตอีก`,
    },
    {
      q: `การรับรอง${d.th} มีอายุการใช้งานไหม?`,
      a: `ตัวตรารับรองไม่มีวันหมดอายุ แต่หน่วยงานปลายทางหลายแห่งกำหนดให้เอกสารต้องออกไม่เกิน 3-6 เดือน จึงควรตรวจเงื่อนไขของหน่วยงานปลายทางก่อนยื่นทุกครั้ง`,
    },
  ];
}
